import { GalaxyFilterInputOverride, GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import {
  ActivityAssociationInterface,
  CrmObjectInterface,
  CustomObjectType,
  FieldType,
  FieldValueInterface as SdkFieldValueInterface,
  GetMultiCrmObjectResponse,
} from '@vendasta/crm';
import { combineLatest, distinctUntilChanged, filter, Observable, of } from 'rxjs';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CustomCellComponent, GalaxyColumnDef, Row } from '@vendasta/galaxy/table';
import { Directive, inject, InjectionToken, input, Type, ViewContainerRef } from '@angular/core';
import { Properties } from 'posthog-js';
import { CardDataType } from '@vendasta/galaxy/contact-info-card';
import { FieldValidation } from './shared-components/model-driven-form/field-validation';
import { User } from '@vendasta/iamv2';
import { ConversationChannel } from '@vendasta/conversation';
import { ConversationChannel as ConversationChannelProto } from '@vendasta/protos/conversation/v1/enums';
import { FormFieldAssociation, PresetFilters } from './shared-components';

type ConversationChannelCompat = ConversationChannel | ConversationChannelProto;
import {
  ActivityTableCustomizationService,
  CrmObjectDisplayService,
  Pipeline,
  TableCustomizationService,
} from './shared-services';
import { NavigationEnd, NavigationSkipped, Router, Scroll } from '@angular/router';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { ObjectNames } from './shared-services/crm-object-display.service';
import { Context } from '@galaxy/meeting-analysis/static';

export { FieldType } from '@vendasta/crm';

export interface ObjectRouteInterface {
  ROOT: string;
  SUBROUTES: {
    PROFILE: string;
  };
}

export interface ProfileCard {
  component: any;
}

export interface FormSectionInjection {
  label: string;
  component: Type<any>;
}

export interface ProfileHeaderComponentInterface {
  crmObjectType: ObjectType | undefined;
  crmObject: CrmObjectInterface | undefined;
}

export interface ProfileHeader {
  component: Type<ProfileHeaderComponentInterface>;
  position?: 'top' | 'bottom';
}

export interface ProfileTitle {
  component: any;
}

export interface CRMSelectAllOptions {
  filters?: GalaxyFilterInterface[];
  search?: string;
  useSelectAll?: boolean;
  totalObjects?: number;
}

export type CRMTableSelection = { mode: 'rows'; rows: Row[] } | { mode: 'all'; options: CRMSelectAllOptions };

export interface UserWithDisplayName extends User {
  displayName: string;
}

export type OnProfilePageInitFunc = (object: CrmObjectInterface) => void;
export type MultiRowActionFunc = (rows: Row[]) => void;
export type SelectAllActionFunc = (rows: Row[], selectOptions?: CRMSelectAllOptions) => void;
export type SingleRowActionFunc = (row: Row) => void;
export type ObjectType =
  | 'Contact'
  | 'Company'
  | 'Activity'
  | 'Opportunity'
  | 'CustomObject'
  | 'File'
  | 'PotentialContact'
  | 'Ticket';
export const ObjectTypes: ObjectType[] = [
  'Contact',
  'Company',
  'Activity',
  'Opportunity',
  'CustomObject',
  'File',
  'PotentialContact',
  'Ticket',
];
export type ObjectDependencyKey = 'contact' | 'company' | 'opportunity' | 'customobject';
// Extra object types that can be associated with an activity
export type ObjectAssociationType = 'Opportunity' | 'User';
export type RowVisibilityFunc = (row: Row, type: ObjectType) => boolean;
export type GetDisplayNameFunc = (crmObject: CrmObjectInterface) => string;

export interface MultiRowAction {
  label: string;
  callback: MultiRowActionFunc;
}

export interface SelectAllAction {
  label: string;
  callback: SelectAllActionFunc;
  selectAllVisible?: boolean;
}

export interface SingleRowAction {
  label: string;
  callback: SingleRowActionFunc;
  visible?: RowVisibilityFunc;
}

export interface CRMRowObject {
  namespace: string;
  objectId: string;
  objectType: ObjectType;
}

export interface TableCustomCell {
  fieldIds: string[];
  customCellComponent: Type<CustomCellComponent>;
  columnDefinition: GalaxyColumnDef;
}

export interface CRMFilterOverride extends GalaxyFilterInputOverride {
  title: string;
}

export interface ActionButton {
  component: any;
}

export type RequiresField<T, K extends keyof T> = T & { [P in K]-?: T[P] };
export type FieldValueInterface = RequiresField<SdkFieldValueInterface, 'fieldId'>;

@Directive()
export abstract class FormInputComponent {
  readOnly = false;
  inlineEdit$: Observable<boolean> = of(false);
  objectType: ObjectType = 'Contact';
  isEditing = false;
  objectId = input('');

  abstract set control(control: UntypedFormControl);
  abstract get control(): UntypedFormControl;

  abstract set formGroup(group: UntypedFormGroup);

  abstract set fieldId(fieldId: string);

  abstract set label(label: string);

  abstract get hasModification(): boolean;

  abstract get value(): FieldValueInterface;
}

/** Base for custom inputs that render no UI; their fields are left out of field search and error states. */
export abstract class HiddenFormInputComponent extends FormInputComponent {}

export interface FormCustomInput {
  fieldId: string;
  component: Type<FormInputComponent>;
}

export interface FormDefaultInputValue {
  fieldId: string;
  value: any;
}

export interface SimplifiedUser {
  userId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string;
  profileImageUrl?: string;
}

export interface UserService {
  searchUsers?(searchTerm?: string): Observable<SimplifiedUser[]>;

  getMultiUsers(userIds: string[]): Observable<SimplifiedUser[]>;
}

/**
 * Availability of edition/entitlement-gated CRM features for the viewed account. Named by what
 * each flag unlocks rather than by the tier that grants it, so a future tier remap is a single
 * change in whichever host service computes this. The host hands it to the lib via
 * CrmDependencies.crmAiFeatures$; read the whole object once and gate each section off it.
 */
export interface CrmAiFeatureAvailability {
  /** 1:1 in-app email composer (Gmail/Outlook) and auto-tracking. */
  nativeEmail: boolean;
  /** Sales email templates. */
  emailTemplates: boolean;
  /** AI email classification (email labeling). */
  emailClassification: boolean;
  /** AI-drafted email replies (the "Draft with AI" action in the reply composer). */
  aiDraftReply: boolean;
}

/** Nothing entitled — the fail-closed default and a convenient startWith seed. */
export const NO_CRM_AI_FEATURES: CrmAiFeatureAvailability = {
  nativeEmail: false,
  emailTemplates: false,
  emailClassification: false,
  aiDraftReply: false,
};

export interface CrmAccessService {
  canViewAllCampaigns$?: Observable<boolean>;
  canModifyAutomations$?: Observable<boolean>;
  hasPartnerPermissions$?: Observable<boolean>;
  hasSalespersonPermissions$?: Observable<boolean>;
  canAccessSales$?: Observable<boolean>;
  canAccessTaskManager$?: Observable<boolean>;
  canAccessAutomations$?: Observable<boolean>;
  canAccessInbox$?: Observable<boolean>;
  // Whether the user may write files in the CRM namespace. Backs the file
  // management upload affordances; hosts that leave it unset are read-only.
  canManageFiles$?: Observable<boolean>;

  canAccessProductPage$?(partnerId: string, marketId: string): Observable<boolean>;

  canAccessCrmObjectType$?(objectType: ObjectType): Observable<boolean>;
}

export const CRM_ACCESS_SERVICE_TOKEN = new InjectionToken<CrmAccessService>(
  'Service for the crm for user access checks',
);

export interface SimplifiedOpportunity {
  opportunityId: string;
  name: string;
}

export interface SalesOpportunityService {
  searchOpportunities(searchTerm: string): Observable<SimplifiedOpportunity[]>;

  getMultiOpportunity(opportunityIds: string[]): Observable<SimplifiedOpportunity[]>;

  setAccountGroupId(accountGroupId: string): void;
}

export interface InitialFilters {
  fieldId: string;
  filterId: string;
}

export interface CreateAssociationFields {
  objectTypes: ObjectType[];
  required?: boolean;
}

export interface PipelineService {
  getPipeline$(pipelineId: string): Observable<Pipeline>;

  getMultiPipeline$(pipelineIds: string[]): Observable<Pipeline[]>;
}

export interface CampaignService {
  navigateToSendCampaign(
    ids: string[],
    filters?: GalaxyFilterInterface[],
    search?: string,
    useSelectAll?: boolean,
    totalContacts?: number,
  ): void;
}

// Dependencies related to specific object types
export interface CrmObjectDependencies {
  multiRowTableActions$?: Observable<MultiRowAction[]>;
  singleRowTableActions$?: Observable<SingleRowAction[]>;
  selectAllTableActions$?: Observable<SelectAllAction[]>;
  onProfilePageInit?: OnProfilePageInitFunc;
  profileCards$?: Observable<ProfileCard[]>;
  formSectionInjections$?: Observable<FormSectionInjection[]>;
  profileHeader$?: Observable<ProfileHeader[]>;
  profileTitle$?: Observable<ProfileTitle[]>;
  actionButtons$?: Observable<ActionButton[]>;
  baseColumnIds?: string[];
  filterInputOverrides$?: Observable<CRMFilterOverride[]>;
  initialFilters$?: Observable<InitialFilters[]>;
  tableCustomCells?: TableCustomCell[];
  formCustomInputs?: FormCustomInput[];
  formDefaultOnCreateInputValues$?: Observable<FormDefaultInputValue[]>;
  showRightHandProfilePanel$?: Observable<boolean>; // default to show right hand profile panel
  showHistoryButton$?: Observable<boolean>; // default to hide history button if not provided
  hideCustomNamespaceFields$?: Observable<boolean>;
  presetFilters$?: Observable<PresetFilters>;
  syncIds$?: Observable<string[]>;
  accountId$?: (crmObject: Observable<GetMultiCrmObjectResponse>) => Observable<string>;
  showCampaigns$?: Observable<boolean>;
  showOpportunityAssociation$?: Observable<boolean>;
  createAssociationFields?: CreateAssociationFields;
  additionalBaseFormFieldIds$?: Observable<string[]>;
  objectSubtype$?: Observable<string>;
  singularObjectName$?: Observable<string>;
  pluralObjectName$?: Observable<string>;
  defaultPageUrl$?: Observable<string>;
  services?: {
    tableCustomizationService?: TableCustomizationService;
    campaignService?: CampaignService;
  };
  getDisplayName?: GetDisplayNameFunc;
}

export interface CrmActivityDependencies {
  filterInputOverrides$?: Observable<CRMFilterOverride[]>;
  initialFilters$?: Observable<InitialFilters[]>;
  showFileAssociations$?: Observable<boolean>;
}

export interface CrmListsDependencies {
  canStartManualAutomation$?: Observable<boolean>;
  singleRowTableActions$?: Observable<SingleRowAction[]>;
}

export interface CrmMultiLocationDependencies {
  multiLocationContext$: Observable<MultiLocationContext>;
  routePrefix$: Observable<string>;
  currentLocationName$: Observable<string>;
  isMultiLocation$: Observable<boolean>;
}

export interface MultiLocationContext {
  groupId: string;
  accountGroupsIds: string[];
}

export interface CrmDependencies {
  appID?: string;
  // the prefix for the route, e.g. /crm. necessary to avoid using relative routing with CRM lib subrouter
  routePrefix$: Observable<string>;

  // A namespace is an instance of the CRM
  // e.g. VUNI for a partner managing their contacts in Partner Center,
  // or AG-12345 for an SMB managing their contacts in Business Center.
  // (This distinction gets weirder in the future when we start merging these Centers,
  // but just roll with it for now.)
  namespace$: Observable<string>;

  // Human-readable name of the current namespace — the partner's name in Partner
  // Center, the business (account group) name in Business Center. Used for
  // audience labels like "All of {name}" instead of the raw namespace id.
  // Optional: apps that don't resolve a name omit it and consumers fall back to
  // a generic label.
  namespaceName$?: Observable<string>;

  // When the CRM is used in Business Center, the namespace is the account group id.
  // This will give you the parent namespace, which is the partner id.
  parentNamespace$: Observable<string>;

  // featureFlags$
  viewProfileFlag$: Observable<boolean>;
  hasCrmAssociationModalFeatureFlag$: Observable<boolean>;
  customObjectFeatureFlag?: string;
  enableNewActivityFeed$: Observable<boolean>;
  hasRestrictedFieldValuesAdminFeatureFlag$?: Observable<boolean>;
  canPartnerAdminCreateOrdersFeatureFlag$?: Observable<boolean>;
  hasCustomObjectFeatureFlag$?: Observable<boolean>;
  hasImportRecordFeatureFlag$?: Observable<boolean>;
  // Swaps the CRM bulk-import page over to the provider-agnostic importer
  // (@galaxy/bulk-actions/importer) behind the CRM adapter. Omitted or false keeps the
  // existing crm-bulk-import-stepper, which stays as the fallback.
  //
  // The page holds a spinner until this answers, rather than rendering the fallback and swapping
  // it out, so it has to know when the answer has arrived. A value delivered synchronously on
  // subscribe is treated as a seed and ignored -- `startWith(false)` is expected and fine. What
  // the page cannot use is an *authoritative* value delivered synchronously on a stream that then
  // neither re-emits nor completes: that reads as a seed that never resolves, and the page falls
  // back on a timer instead of on the answer. Emit asynchronously, or complete.
  hasGenericImporterFeatureFlag$?: Observable<boolean>;
  /**
   * Routes "Export all" through the async bulk-actions exporter and shows its tray. Must not be seeded
   * with a synchronous default (no startWith(false)): its first value is read as the answer.
   */
  hasAsyncExporterFeatureFlag$?: Observable<boolean>;
  showCustomObjects$?: Observable<boolean>;
  hasCustomObjectPipelineFeatureFlag$?: Observable<boolean>;

  customObjectPipelineConfigUrl$?: Observable<string>;

  hasOrganizeLayoutFeatureFlag$?: Observable<boolean>;
  hasBasicInventoryManagementFeatureFlag$?: Observable<boolean>;
  hasInPersonMeetingSchedulingFeatureFlag$?: Observable<boolean>;

  // the unified user id of the current user
  currentUserId$: Observable<string>;

  locale$?: Observable<string>;

  // the market/retail-configuration currency, used when creating ad-hoc inventory items
  retailCurrency$?: Observable<string>;

  // ISO 3166-1 alpha-2 country the phone renderer preselects on an empty field — the partner's
  // country in Partner Center, the business's in Business Center. Values that libphonenumber-js
  // doesn't recognize are ignored, and hosts that omit this keep the 'US' fallback.
  defaultPhoneCountryCode$?: Observable<string>;

  // gates the opportunity-page Create Invoice action; omitted hosts hide it
  canCreateInvoiceFromOpportunity$?: Observable<boolean>;

  // dependencies by object type
  activity?: CrmActivityDependencies;
  company?: CrmObjectDependencies;
  contact?: CrmObjectDependencies;
  task?: CrmObjectDependencies;
  opportunity?: CrmObjectDependencies;
  lists?: CrmListsDependencies;
  customobject?: CrmObjectDependencies;
  potentialcontact?: CrmObjectDependencies;

  showBackToAccountsButton?: boolean;

  // dependencies for namespace specific shared-services
  services?: {
    userService?: UserService;
    opportunityService?: SalesOpportunityService; // Falls back to crmObjectService if not provided
    accessService?: CrmAccessService;
    pipelineService?: PipelineService;
    defaultAssistantAvatarIcon?: string;
  };

  hasInboxAccess$: Observable<boolean>;
  canShowInboxForContact$: Observable<boolean>;
  openAccountGroupConversation?: (accountGroupId: string) => void | Promise<void>;
  openContactConversation?: (
    namespace: string,
    parentNamespace: string,
    contactId: string,
    channel: ConversationChannelCompat,
  ) => void | Promise<void>;

  // Helps determine whether a partner has access to a feature
  hasAccessToFeature$?: (feature: string) => Observable<boolean>;
  openRestrictedDialog?: (featureId: string) => void;
  showLeadProspector$?: Observable<boolean>;

  // Client specific actions to shared components
  customActions?: {
    // navigates to the fulfillment projects page for a given company
    navigateToFulfillmentProjects?: (row: CRMRowObject) => void;
    openOpportunitiesDetails?: (opportunityId: string, accountGroupId: string) => void;
  };

  isMeetingIconNeeded$?: Observable<boolean>;
  meetingAction?: (
    contactId: string,
    currentUserId: Observable<string>,
    namespace: Observable<string>,
    parentNamespace: Observable<string>,
  ) => void;
  uploadRecordingAction?: (contactId: string, namespace: Observable<string>, context: Context) => void;
  automationsSideMenuComponent?: () => Promise<Type<unknown>>;

  customObjectTypes$?: Observable<CustomObjectType[]>;
  bulkImportDefaultValues$?: Observable<FieldValueInterface[]>;

  connectedEmailProvider$?: Observable<'gmail' | 'outlook' | null>;

  availableEmailProviders$?: Observable<{ gmail: boolean; outlook: boolean }>;

  // CRM AI feature availability for the viewed account, resolved by the host (CRM AI editions in
  // the Business App, the crm-native-email entitlement in Partner Center). Gate email surfaces off
  // this — e.g. the contact email button uses nativeEmail to choose the composer vs. a mailto link.
  crmAiFeatures$?: Observable<CrmAiFeatureAvailability>;

  openEmailComposer?: (contactIds: string[], emails: string[], provider: 'gmail' | 'outlook') => void;

  openEmailReply?: (
    contactIds: string[],
    emails: string[],
    subject: string,
    body?: string,
    visibility?: string,
    threadId?: string,
    sourceProvider?: string,
    associations?: ActivityAssociationInterface[],
    inReplyToMessageId?: string,
    ccContactIds?: string[],
    ccEmails?: string[],
  ) => void;
}

export const CrmInjectionToken = new InjectionToken<CrmDependencies>('dependencies for the crm');

export const CrmObjectInjectionToken = new InjectionToken<CrmObjectDependencies>(
  'dependencies for the crm object type',
);

export const CrmMultilocationInjectionToken = new InjectionToken<CrmMultiLocationDependencies>(
  'dependencies for the multi-location context',
);

/**
 * Interface that AI Summary components must implement to be used with CrmActivitiesTimelineComponent.
 * Components implementing this interface will receive crmObjectId and crmObjectType as inputs.
 */
export interface AiSummaryComponentLike {
  crmObjectId: ReturnType<typeof input<string>>;
  crmObjectType: ReturnType<typeof input<ObjectType | null>>;
}

/**
 * Injection token for providing an optional AI Summary component to CrmActivitiesTimelineComponent.
 * When provided, the component will be rendered via ngComponentOutlet.
 * Consumers can provide this token at the module/component level to enable AI Summary functionality.
 */
export const AI_SUMMARY_COMPONENT = new InjectionToken<Type<AiSummaryComponentLike> | null>(
  'Optional AI Summary component for CrmActivitiesTimelineComponent',
);

export function crmObjectDependenciesTokenGenerator(
  crm: CrmDependencies,
  objectTableCustomizationService: TableCustomizationService,
  objectUrlRoot: string,
  crmObjectDependencies?: CrmObjectDependencies,
): CrmObjectDependencies {
  if (!crmObjectDependencies) {
    return {} as CrmObjectDependencies;
  }
  const crmObjectDisplayService = inject(CrmObjectDisplayService);
  const router = inject(Router);
  const objectSubtype$: Observable<string> = router.events.pipe(
    map((event) => {
      if (event instanceof Scroll) {
        return event.routerEvent;
      }
      return event;
    }),
    filter((event) => event instanceof NavigationEnd || event instanceof NavigationSkipped),
    map((event: NavigationEnd | NavigationSkipped) => {
      const url = event.url;
      const customObjectTypeRegex = /(CustomObjectTypeID-[-0-9a-zA-Z]*)/;
      const capture = customObjectTypeRegex.exec(url);
      return capture && capture.length > 1 ? capture[1] : '';
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );
  if (!crmObjectDependencies.objectSubtype$) {
    crmObjectDependencies.objectSubtype$ = objectSubtype$;
  }

  const names$: Observable<ObjectNames> = crmObjectDependencies.objectSubtype$.pipe(
    switchMap((objectSubtype: string | undefined) =>
      crmObjectDisplayService.getObjectNames(crm.namespace$, objectSubtype),
    ),
    shareReplay(1),
  );
  crmObjectDependencies.singularObjectName$ = names$.pipe(map((names: ObjectNames) => names.singularObjectName));
  crmObjectDependencies.pluralObjectName$ = names$.pipe(map((names: ObjectNames) => names.pluralObjectName));

  crmObjectDependencies.defaultPageUrl$ = combineLatest([crm.routePrefix$, objectSubtype$]).pipe(
    map(([routePrefix, objectSubtype]) => {
      if (objectSubtype) {
        return `${routePrefix}/${objectUrlRoot}/${objectSubtype}`;
      }
      return `${routePrefix}/${objectUrlRoot}`;
    }),
    shareReplay(1),
  );

  crmObjectDependencies.services = {
    tableCustomizationService: objectTableCustomizationService,
  };

  return crmObjectDependencies as CrmObjectDependencies;
}

export interface CompanyProfileCardData {
  company_id: string;
  account_group_id?: string;
  group_id?: string;
  name?: string;
  id?: string;
  objectType?: string;
}

export const COMPANY_PROFILE_CARD_DATA_TOKEN = new InjectionToken<CompanyProfileCardData>('company-profile-card-data');

export interface PageAnalyticsDependencies {
  trackEvent(action: string, properties?: Properties): void;
}

export const PageAnalyticsInjectionToken = new InjectionToken<PageAnalyticsDependencies>(
  'dependencies for page analytics',
);
export type ActivityType = 'Note' | 'Email' | 'Call' | 'Meeting' | 'Task' | 'Communication';
export type CommunicationSubType = 'SMS' | 'LinkedIn' | 'Inbox';
export type RecordChangeType = 'CREATED' | 'IMPORTANT_FIELD_UPDATED';

export interface CardData extends CardDataType {
  tags?: string[];
  hideAvatar?: boolean;
  accountGroupCompanyName?: string;
  accountGroupCompanyId?: string;
  associationId?: string;
}

export interface DropdownOptionInterface {
  label?: string;
  value?: string;
}

export interface FieldSchemaInterface {
  fieldId: string;
  fieldType: FieldType;
  fieldName: string;
  fieldDescription: string;
  dropdownOptions: DropdownOptionInterface[];
  currencyCode: string;
  value: any;
  readonly: boolean;
  hasDefaultValue?: boolean;
  namespace?: string;
}

export interface AssociationFormField {
  type: ObjectType;
  associations: FormFieldAssociation[];
  associationsRequired?: boolean;
  subtype?: string;
}

export interface FieldInterface {
  fieldId: string;
  name: string;
  value: any;
  description: string;

  // Gets the field value for the backend
  fieldValue(): FieldValueInterface;

  // Checks if the field received a update
  hasModification(): boolean;
}

export interface FieldGroupInterface {
  fieldGroupId?: string;
  description?: string;
  fieldIds: string[];
}

export const ActivityTaskStatus = {
  Open: 'Open',
  Completed: 'Completed',
};

export interface FieldOptionsDependencies {
  getFieldOptions(fieldId: string, search: string): Observable<string[]>;
}

export const FieldOptionsInjectionToken = new InjectionToken<FieldOptionsDependencies>(
  'dependencies for getting field options in the model-driven form',
);

export enum LoadingStatus {
  LOADING = 1,
  OK = 2,
  EMPTY = 3,
  ERROR = 4,
}

export enum SavingStatus {
  NOT_SAVING = 1,
  SAVING = 2,
  OK = 3,
  ERROR = 4,
}

export enum NullValue {
  NULL_VALUE = 0,
}

export interface RenderableFieldInterface extends FieldInterface {
  formControl: AbstractControl;
  validation: FieldValidation;
  objectType: ObjectType;
  readOnly: boolean;
  label?: string;
  hidden?: boolean;

  // Chooses the correct component to display information
  render(viewContainerRef: ViewContainerRef): void;

  setValidation(validation: FieldValidation): void;
}

export interface RenderableFieldGroupInterface {
  fieldGroupId?: string;
  description?: string;
  fields: RenderableFieldInterface[];
  editableFieldCount: number;
}

export interface FieldConfigInterface {
  fieldSchemas?: FieldSchemaInterface[];
  fieldGroups?: FieldGroupInterface[];
}

export interface ProfileCardData {
  id: string;
  name: string;
  objectType: ObjectType;
}

export interface ProfilePageData {
  accountGroupId?: string;
  crmObject?: CrmObjectInterface;
  onChangeCallback?: (crmObject: CrmObjectInterface) => void;
}

export const PROFILE_PAGE_DATA_TOKEN = new InjectionToken<ProfilePageData>(
  'CRM object data loaded in the profile page',
);

export const PrimaryAccountGroupIDForCrmObject = new InjectionToken<Observable<string>>(
  'primary account group ID for crm object in a given context',
);

export const TaskTableCustomizationServiceToken = new InjectionToken<ActivityTableCustomizationService>(
  'Injection token for the task table customization service to be provided in the dynamic CRM module',
);

export type ListObjectView = 'board' | 'table' | 'unknown';
